import React, { useState } from "react";

import { i18n } from "../../translate/i18n";
import { formatPhoneNumber } from "../../helpers/formatPhoneNumber";

import ContactModal from "../ContactModal";
import ContactDrawerSkeleton from "../ContactDrawerSkeleton";
import MarkdownWrapper from "../MarkdownWrapper";
import { Button } from "../ui";

// Icons
const CloseIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const ContactDrawer = ({ open, handleDrawerClose, contact, loading }) => {
  const [modalOpen, setModalOpen] = useState(false);

  if (!open) return null;

  return (
    <div className="w-80 h-full flex flex-col border-l border-dark-200 dark:border-dark-700 bg-dark-50 dark:bg-dark-900">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-dark-200 dark:border-dark-700 bg-dark-100 dark:bg-dark-800">
        <button
          onClick={handleDrawerClose}
          className="p-1.5 text-dark-500 hover:text-dark-700 dark:text-dark-400 dark:hover:text-dark-200 hover:bg-dark-200 dark:hover:bg-dark-700 rounded-lg transition-colors"
        >
          <CloseIcon className="w-5 h-5" />
        </button>
        <h2 className="text-sm font-semibold text-dark-900 dark:text-white">
          {i18n.t("contactDrawer.header")}
        </h2>
      </div>

      {/* Content */}
      {loading ? (
        <ContactDrawerSkeleton />
      ) : (
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Contact Card */}
          <div className="bg-white dark:bg-dark-800 rounded-lg border border-dark-200 dark:border-dark-700 p-6 flex flex-col items-center">
            {/* Avatar */}
            {contact.profilePicUrl ? (
              <img
                src={contact.profilePicUrl}
                alt={contact.name}
                className="w-40 h-40 rounded-full object-cover mb-4"
              />
            ) : (
              <div className="w-40 h-40 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white font-bold text-5xl mb-4">
                {contact.name?.charAt(0)?.toUpperCase() || "?"}
              </div>
            )}

            {/* Name */}
            <h3 className="text-lg font-semibold text-dark-900 dark:text-white text-center">
              {contact.name}
            </h3>

            {/* Phone */}
            <a
              href={`tel:${formatPhoneNumber(contact.number)}`}
              className="text-sm text-primary-600 dark:text-primary-400 hover:underline mb-4"
            >
              {formatPhoneNumber(contact.number)}
            </a>

            {/* Edit Button */}
            <Button
              variant="outlined"
              size="small"
              onClick={() => setModalOpen(true)}
            >
              {i18n.t("contactDrawer.buttons.edit")}
            </Button>
          </div>

          {/* Extra Info */}
          {contact?.extraInfo?.length > 0 && (
            <div className="bg-white dark:bg-dark-800 rounded-lg border border-dark-200 dark:border-dark-700 p-4">
              <h4 className="text-sm font-semibold text-dark-900 dark:text-white mb-3">
                {i18n.t("contactDrawer.extraInfo")}
              </h4>

              <div className="space-y-2">
                {contact.extraInfo.map((info) => (
                  <div
                    key={info.id}
                    className="bg-dark-50 dark:bg-dark-900 rounded border border-dark-200 dark:border-dark-700 p-3"
                  >
                    <label className="text-xs text-dark-500 dark:text-dark-400 block mb-1">
                      {info.name}
                    </label>
                    <div className="text-sm text-dark-700 dark:text-dark-200">
                      <MarkdownWrapper>{info.value}</MarkdownWrapper>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Contact Modal */}
      <ContactModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        contactId={contact.id}
      />
    </div>
  );
};

export default ContactDrawer;
